import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import {
  Plus, Building2, MapPin, CheckCircle2, AlertTriangle, XCircle,
  ChevronRight, Search, Loader2, BookOpen, BarChart3, Upload, Users,
  Sparkles, Shield, Calendar, Camera, Pencil, TrendingUp, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { InfoModal } from '../../common/InfoModal';
import US_STATES from '../../../constants/usStates';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import AitPortfolioAnalytics from './AitPortfolioAnalytics';
import AitGlossaryModal from './AitGlossaryModal';

const COMPLIANCE_COLORS = {
  compliant: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle2 },
  at_risk: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: AlertTriangle },
  breach: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle },
  fixed_set_aside: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: Shield },
  no_data: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', icon: Building2 },
};

function AitDashboard() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { properties, fetchProperties, createProperty, loading } = useAit();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '', address: '', city: '', state: '', zip_code: '', total_units: '',
    msa_election: 'average_income',
  });
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('properties');
  const [glossaryTopic, setGlossaryTopic] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    const result = await createProperty(formData);
    if (result.success) {
      toast.success('Property created');
      setShowCreateForm(false);
      setFormData({ name: '', address: '', city: '', state: '', zip_code: '', total_units: '', msa_election: 'average_income' });
      navigate(`/dashboard/ait/${result.property.id}`);
    } else {
      toast.error(result.error || 'Failed to create property');
    }
    setCreating(false);
  };

  const filtered = properties.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatus = (property) => {
    const status = property.compliance_status;
    return COMPLIANCE_COLORS[status] || COMPLIANCE_COLORS.no_data;
  };

  const isDark = theme === 'dark';

  return (
    <div className="h-full overflow-y-auto">
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-dm-serif text-gray-900 dark:text-white">Average Income Test</h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage LIHTC properties and track AIT compliance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 shadow-sm transition-all text-xs"
          >
            <BookOpen className="w-3 h-3" />
            Guide
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus size={16} /> Add Property
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className={`flex gap-1 mb-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {[
          { key: 'properties', label: 'Properties', icon: Building2 },
          { key: 'analytics', label: 'Portfolio Analytics', icon: TrendingUp },
        ].map(tab => {
          const TabIcon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                active
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              <TabIcon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && <AitPortfolioAnalytics />}

      {/* Properties Tab */}
      {activeTab === 'properties' && <>
      {/* Search */}
      {properties.length > 0 && (
        <div className="relative mb-4">
          <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm ${
              isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
          />
        </div>
      )}

      {/* Loading */}
      {loading && properties.length === 0 && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-blue-500" size={32} />
        </div>
      )}

      {/* Empty state */}
      {!loading && properties.length === 0 && (
        <div className={`text-center py-20 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <Building2 size={48} className={`mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className="text-lg font-medium mb-2 text-gray-900 dark:text-white">No properties yet</h3>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Add your first LIHTC property to start tracking AIT compliance.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Add Property
          </button>
        </div>
      )}

      {/* Property cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(property => {
          const status = getStatus(property);
          const StatusIcon = status.icon;
          return (
            <button
              key={property.id}
              onClick={() => navigate(`/dashboard/ait/${property.id}`)}
              className={`text-left p-4 rounded-xl border transition-all hover:shadow-md ${
                isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-sm truncate pr-2 text-gray-900 dark:text-white">{property.name}</h3>
                <ChevronRight size={16} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
              </div>
              {property.address && (
                <div className={`flex items-center gap-1.5 text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <MapPin size={12} />
                  <span className="truncate">{property.address}{property.city ? `, ${property.city}` : ''}{property.state ? ` ${property.state}` : ''}</span>
                </div>
              )}
              {/* Stats row */}
              <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <span className="whitespace-nowrap">{property.building_count || 0} bldg</span>
                <span className="whitespace-nowrap">{property.unit_count || property.total_units || '?'} units</span>
                {property.occupied_count != null && (
                  <>
                    <span className="whitespace-nowrap text-green-500">{property.occupied_count} occ</span>
                    {(property.leased_count || 0) > 0 && <span className="whitespace-nowrap text-blue-500">{property.leased_count} leased</span>}
                    {(property.notice_count || 0) > 0 && <span className="whitespace-nowrap text-orange-500">{property.notice_count} notice</span>}
                    <span className={`whitespace-nowrap ${property.vacant_count > 0 ? 'text-orange-500' : ''}`}>{property.vacant_count} vac</span>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {property.msa_election === 'average_income' ? 'Average Income' :
                   property.msa_election === '40_60' ? '40/60' :
                   property.msa_election === '20_50' ? '20/50' :
                   property.msa_election === '25_60' ? '25/60 NYC' : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
                    <StatusIcon size={12} />
                    {property.compliance_status === 'compliant' ? 'Compliant' :
                     property.compliance_status === 'at_risk' ? 'At Risk' :
                     property.compliance_status === 'breach' ? 'Breach' :
                     property.compliance_status === 'fixed_set_aside' ? 'Fixed Set-Aside' :
                     property.compliance_status === 'no_data' ? 'No Data' : 'No Data'}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setGlossaryTopic('compliance'); }}
                    className={`p-0.5 rounded-full transition ${isDark ? 'text-gray-500 hover:text-blue-400 hover:bg-gray-700' : 'text-gray-400 hover:text-blue-500 hover:bg-gray-100'}`}
                    aria-label="What do these statuses mean?"
                    title="What do these statuses mean?"
                  >
                    <Info size={13} />
                  </button>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      </>}

      {/* Create property modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">New AIT Property</h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Property Name *</label>
                <input
                  type="text" required value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  placeholder="e.g., Stratford Point Apartments"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Address <span className="text-[10px] text-gray-500 font-normal">— picking one fills City, State, ZIP</span>
                </label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(val) => setFormData(prev => ({ ...prev, address: val }))}
                  onPlaceSelect={(p) => setFormData(prev => ({
                    ...prev,
                    address: p.address,
                    city: p.city || prev.city,
                    state: p.state || prev.state,
                    zip_code: p.zip_code || prev.zip_code,
                  }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">City</label>
                  <input
                    type="text" value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">State</label>
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  >
                    <option value="">Select</option>
                    {US_STATES.map(s => (
                      <option key={s.value} value={s.value}>{s.value}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">ZIP</label>
                  <input
                    type="text" value={formData.zip_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Total Units</label>
                  <input
                    type="number" value={formData.total_units}
                    onChange={(e) => setFormData(prev => ({ ...prev, total_units: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Set-Aside Election</label>
                  <select
                    value={formData.msa_election}
                    onChange={(e) => setFormData(prev => ({ ...prev, msa_election: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  >
                    <option value="average_income">Average Income</option>
                    <option value="40_60">40/60</option>
                    <option value="20_50">20/50</option>
                    <option value="25_60">25/60 (NYC)</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowCreateForm(false)}
                  className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                  Cancel
                </button>
                <button type="submit" disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Guide Modal */}
      <InfoModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Average Income Test Guide"
      >
        <div className="space-y-5">
          {/* Overview */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna">What is the Average Income Test?</h4>
            <p className="text-base font-rasa mb-2">
              The AIT (IRC Section 42(g)(1)(C)) allows LIHTC properties to designate units at different AMI levels (20%-80%) as long as the <strong>average</strong> of all designated units is <strong>60% or below</strong>.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm font-rasa">
              <strong>Valid Designations:</strong> 20%, 30%, 40%, 50%, 60%, 70%, 80% AMI
            </div>
          </section>

          {/* Quick Start */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-500" /> Quick Start
            </h4>
            <ol className="list-decimal list-inside space-y-1.5 text-base font-rasa">
              <li><strong>Create a property</strong> — Click "Add Property" and enter basic details</li>
              <li><strong>Upload a rent roll</strong> — Import units from Excel/CSV with AI-powered parsing</li>
              <li><strong>Review compliance</strong> — Click a building card to see the compliance dashboard</li>
              <li><strong>Manage designations</strong> — Redesignate, swap, or convert units as needed</li>
              <li><strong>Save snapshots</strong> — Capture compliance state for audit records</li>
            </ol>
          </section>

          {/* Uploading Data */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Upload className="w-4 h-4 text-green-500" /> Uploading & Importing
            </h4>
            <p className="text-base font-rasa mb-2"><strong>Rent Roll Upload</strong></p>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li>Upload Excel (.xlsx, .xls) or CSV files with rent roll data</li>
              <li>AI detects columns: unit number, BIN, bedrooms, designation, income, rent, and more</li>
              <li>Import creates buildings and units automatically</li>
              <li>Financial fields (income %, rent, move-in date, recert date) are imported when available</li>
              <li>Re-importing updates existing units without blanking fields</li>
              <li>Current month's designation auto-populates in the monthly tracking grid</li>
            </ul>
            <p className="text-base font-rasa mt-3 mb-2"><strong>TIC Upload</strong></p>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li>Upload Tenant Income Certification (TIC) forms as PDF or image files</li>
              <li>AI extracts unit number, BIN, designation, income, rent, utility allowance, household size, and dates</li>
              <li>Batch upload multiple TIC files at once from the property view</li>
              <li>Single-unit TIC upload available from the building detail edit modal</li>
              <li>BIN and unit numbers are automatically normalized for consistent matching</li>
            </ul>
          </section>

          {/* Unit Management */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Pencil className="w-4 h-4 text-purple-500" /> Unit Management
            </h4>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li><strong>View unit details</strong> — Click any row in the unit table to expand it and see all data: income, income limit, % AMI, rent, utility allowance, square footage, household size, move-in date, recert date, tenant ID, and occupancy</li>
              <li><strong>Edit unit</strong> (pencil icon) — Enter dollar amounts for household income and income limit (AMI % auto-calculates). Use the search icon on Income Limit to AI-lookup HUD limits for your area. Also edit rent, tenant ID, household size, dates, occupancy, and IQG status.</li>
              <li><strong>Redesignate</strong> — Change a unit's AMI designation</li>
              <li><strong>Swap</strong> — Exchange designations between comparable units</li>
              <li><strong>Convert</strong> — Move units between qualified and market rate</li>
              <li><strong>Occupancy</strong> — Change directly via dropdown in the unit table</li>
            </ul>
          </section>

          {/* Compliance Dashboard */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Shield className="w-4 h-4 text-orange-500" /> Compliance Dashboard
            </h4>
            <p className="text-base font-rasa mb-2">Each building detail page shows:</p>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li><strong>Average Income</strong> — Must be ≤ 60%, with headroom shown</li>
              <li><strong>Compliance position</strong> — 7 levels from Compliant Stable to Noncompliant Irreversible</li>
              <li><strong>Flexibility index</strong> — How much room for changes (High → None)</li>
              <li><strong>Pressure Level</strong> — Risk of falling out of compliance</li>
              <li><strong>Concentration risks</strong> — Alerts for high-band clustering, low-band deficiency, etc.</li>
              <li><strong>Over-income detection</strong> — Flagged units with tiered correction pathways (Tier A-C)</li>
            </ul>
          </section>

          {/* Monthly Tracking */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-500" /> Monthly Tracking
            </h4>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li>12-month grid showing each unit's designation per month</li>
              <li><strong>Auto carry-forward</strong> — Months up to the current month auto-fill from the last known designation</li>
              <li>Future months stay blank (haven't occurred yet)</li>
              <li>Click any cell to change a designation for that month</li>
              <li><strong>Year carry-forward</strong> — Button to copy last year's data into January of a new year</li>
              <li>Use for IRS 8609 filing and audit documentation</li>
            </ul>
          </section>

          {/* Snapshots */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Camera className="w-4 h-4 text-indigo-500" /> Snapshots
            </h4>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li>Save point-in-time compliance records via the <strong>Snapshots</strong> tab</li>
              <li>View full history: date, Average Income, compliance position, flexibility, pressure</li>
              <li>Expand any snapshot to see Designation Distribution and concentration risks</li>
              <li>Save snapshots monthly or before/after significant changes</li>
            </ul>
          </section>

          {/* Portfolio Analytics */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" /> Portfolio Analytics
            </h4>
            <p className="text-base font-rasa mb-2">Switch to the <strong>Analytics</strong> tab for a cross-property dashboard:</p>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li><strong>Compliance Summary</strong> — Card counts for Compliant, At Risk, Breach, Fixed Set-Aside, and No Data properties</li>
              <li><strong>Compliance Distribution</strong> — Donut chart showing the proportion of each compliance status</li>
              <li><strong>Headroom by Property</strong> — Horizontal bar chart comparing headroom across properties, color-coded by risk level</li>
              <li><strong>Alerts</strong> — Paginated list of recertifications due, over-income flags, pressure warnings, and concentration risks across all properties</li>
              <li><strong>Property Cards</strong> — Grid view of each property with average income, headroom, flexibility, pressure, and your role</li>
              <li><strong>Average Income Trends</strong> — Line chart tracking average income over time from saved snapshots (3/6/12 month view)</li>
              <li><strong>Filter Properties</strong> — Toggle individual properties in or out of your analytics view (exclusions persist across sessions)</li>
            </ul>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm font-rasa mt-2">
              <strong>Tip:</strong> Save snapshots regularly from building views to populate the trends chart with historical data.
            </div>
          </section>

          {/* AI Assistant */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-500" /> AI Compliance Assistant
            </h4>
            <p className="text-base font-rasa mb-2">Click the JPRO Assistant button on any property or building page. Suggested prompts include:</p>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li><strong>Look up AMI limits</strong> — Searches HUD income and rent limits for your property's area</li>
              <li><strong>Compliance summary</strong> — Overview of current status and risks</li>
              <li><strong>Optimize designations</strong> — Suggestions to improve headroom and flexibility</li>
              <li><strong>Over-income options</strong> — Correction pathways ranked by disruption</li>
            </ul>
            <p className="text-base font-rasa mt-2">The AI can also model what-if scenarios, suggest unit swaps, and provide audit-ready documentation language.</p>
          </section>

          {/* Team Access */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna flex items-center gap-2">
              <Users className="w-4 h-4 text-green-500" /> Collaboration
            </h4>
            <ul className="list-disc list-inside space-y-1 text-base ml-2 font-rasa">
              <li>Invite team members via email</li>
              <li>Roles: <strong>Owner</strong> (full control), <strong>Editor</strong> (modify data), <strong>Viewer</strong> (read-only)</li>
              <li>All members can use the JPRO assistant</li>
            </ul>
          </section>

          {/* Tips */}
          <section>
            <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white font-unna">Tips for Success</h4>
            <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-rasa space-y-1">
              <p>- Keep headroom above 2-3% as a safety buffer</p>
              <p>- Avoid clustering too many units at 70-80% (triggers concentration risk)</p>
              <p>- Enter household income after recertifications to catch over-income early</p>
              <p>- Use the AI's "Look up AMI limits" prompt to verify current HUD thresholds</p>
              <p>- Save snapshots before making major designation changes</p>
              <p>- Re-upload rent rolls to batch-update financial data</p>
            </div>
          </section>
        </div>
      </InfoModal>

      <AitGlossaryModal
        isOpen={!!glossaryTopic}
        topic={glossaryTopic}
        onClose={() => setGlossaryTopic(null)}
      />
    </div>
    </div>
  );
}

export default AitDashboard;
